import { ShieldAlert } from "lucide-react";
import { SG7TacticalPanel } from "./sg7";
import { useTranslation } from "react-i18next";

interface DisciplineData {
  yellow_cards: number;
  red_cards: number;
}

interface DisciplineCardProps {
  disciplina?: Record<string, DisciplineData>;
  homeTeamName?: string;
  awayTeamName?: string;
  className?: string;
}

// Normaliza nome removendo acentos para comparação
const normalizeTeamName = (name: string): string =>
  name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Procura dados da equipa com tolerância a acentos
const findTeamData = (
  disciplina: Record<string, DisciplineData>,
  teamName: string,
  fallbackKey: 'home' | 'away'
): DisciplineData | null => {
  // Match exacto primeiro
  if (disciplina[teamName]) return disciplina[teamName];
  
  // Fallback "home"/"away"
  if (disciplina[fallbackKey]) return disciplina[fallbackKey];
  
  // Procura por nome normalizado (sem acentos)
  const normalizedSearch = normalizeTeamName(teamName);
  const matchingKey = Object.keys(disciplina).find(
    key => key !== 'home' && key !== 'away' && normalizeTeamName(key) === normalizedSearch
  );
  
  return matchingKey ? disciplina[matchingKey] : null;
};

export function DisciplineCard({ 
  disciplina, 
  homeTeamName,
  awayTeamName,
  className 
}: DisciplineCardProps) {
  const { t } = useTranslation('analysis');
  
  // Use translated defaults if team names not provided
  const home = homeTeamName || t('defaults.home');
  const away = awayTeamName || t('defaults.away');

  if (!disciplina) return null;

  const homeData = findTeamData(disciplina, home, 'home');
  const awayData = findTeamData(disciplina, away, 'away');

  if (!homeData && !awayData) return null;

  const renderCards = (data: DisciplineData) => (
    <div className="flex items-center gap-2">
      <span className="flex items-center gap-1">
        <span className="w-3 h-4 bg-yellow-400 rounded-sm" />
        <span className="text-sm font-bold text-[hsl(var(--sg7-text))]">{data.yellow_cards}</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="w-3 h-4 bg-red-500 rounded-sm" />
        <span className="text-sm font-bold text-[hsl(var(--sg7-text))]">{data.red_cards}</span>
      </span>
    </div>
  );

  return (
    <SG7TacticalPanel className={className}>
      <div className="p-4">
        <h3 className="text-sm font-bold flex items-center gap-2 text-[hsl(var(--sg7-text))] mb-3">
          <ShieldAlert className="h-4 w-4 text-[hsl(var(--sg7-gold))]" />
          {t('cards.discipline')}
        </h3>
        <div className="space-y-3">
          {homeData && (
            <div className="flex items-center justify-between p-2 bg-[hsl(var(--sg7-surface-2))] rounded">
              <span className="text-sm font-medium text-[hsl(var(--sg7-text))]">{home}</span>
              {renderCards(homeData)}
            </div>
          )}
          {awayData && (
            <div className="flex items-center justify-between p-2 bg-[hsl(var(--sg7-surface-2))] rounded">
              <span className="text-sm font-medium text-[hsl(var(--sg7-text))]">{away}</span>
              {renderCards(awayData)}
            </div>
          )}
        </div>
      </div>
    </SG7TacticalPanel>
  );
}
